import * as aspSettings from './aspSettings';
import * as aspObjects from './aspObjects';
import * as enums from '../common/enums';
import * as units from '../common/units';
import * as utils from '../common/utils';
import * as consts from '../common/consts';
import * as settings from '../common/settings';
import { AspObject, ObjectType } from './AspObject';
import { IObjectsPool, ISensorsPool } from './interfaces';
import { Location } from '../common/Location';
import { SensorCoreObject } from './SensorCoreObject';
import { ClsArmService } from 'src/core/app/asp/cls-arm.service';
import { AppInjector } from '../common/inject';
import { ReturnStatement } from '@angular/compiler';


function getMsgColor(hexColor: string) {
  let rgbColor = NeWMI.Pure.Services.Draw.hexToRgb(hexColor);
  return [rgbColor.r / 255, rgbColor.g / 255, rgbColor.b / 255];
}

class TrackTrail {
  points: Array<any> = new Array();
  times: Array<number> = new Array();
  addPoint(geometry) {
    let now = Date.now();

    // ignore point if less than 1 sec since last point
    let lastAge = ((now - this.times[this.times.length - 1]) / 1000);
    if (lastAge < 1)
      return;

    if (this.points.length > aspSettings.trackTrailsCount()) {
      this.points.shift();
      this.times.shift();
    }

    const newPoint = new NeWMI.Geometry.Point({ x: geometry.x, y: geometry.y, z: geometry.z });
    this.points.push(newPoint);
    this.times.push(now);
  }

  // Consider trail length user setting
  getVisibleTrail() {
    let now = Date.now();
    let trailLength = settings.userSettings.trackTrailLength;
    for (let i = 0; i < this.times.length; i++) {
      let age = ((now - this.times[i]) / 1000);
      if (age <= trailLength) {
        // found age within required trail length
        return new NeWMI.Geometry.Polyline({ points: this.points.slice(i) });
      }
    }
    // required trail length is less than any existing trail points
    return new NeWMI.Geometry.Polyline({ points: this.points.slice(this.points.length - 1) });
  }

};

export interface CorrEntityID {
  systemID: number;
  entityID: number;
  inManeuver: boolean; // relevant for plots
}

export class TrackCoreObject extends AspObject {
  public MAX_SPEED_MS = settings.MAX_SPEED_KNOTS * units.KNOT_TO_METER_SEC;

  public ForceUpdate: boolean = false;
  public dontupdateloc: boolean = false;

  //Main Track Details
  trackType: enums.TrackType = enums.TrackType.Unidentified;
  trackStatus: enums.TrackStatus = 0;
  timeStamp: Date;
  TimeOfArrival: number; // mills since 1970...
  hostility: enums.Hostility = 0;
  public userSetHostility = false;
  trackPltf: enums.AirPltf = enums.AirPltf.NoStatement;
  isVisible: boolean = true;
  isInDm: boolean = true;

  // vector
  vectorLat: number = 0;
  vectorLong: number = 0;
  vectorAlt: number = 0;
  vectorHeading: number = 0;

  // lost: boolean = false;
  maneuver: boolean = false;
  boostPhase: boolean = false;

  //Sensor list
  snsObjList: Array<SensorCoreObject> = [];
  // this array is backup...
  snsIdList: Array<number> = [];

  trackingStatus: string;

  //External Track data
  isExternal: boolean = false;
  externalTN: number = 0;
  remoteHostility: enums.Hostility = 0;

  //Velocity
  velocityX: number = 0;
  velocityY: number = 0;
  velocityZ: number = 0;

  //IFF SECTION
  iffAvailable: boolean = false;
  mode1Valid: boolean = false;
  mode1: number;
  mode2Valid: boolean = false;
  mode2: number;
  mode3Valid: boolean = false;
  mode3: number;
  mode4Valid: boolean = false;
  modeSValid: boolean = false;
  modeSAcIdent: string = "";
  modeSAddress: number = 0;
  modeSAltitude: number = 0;
  modeSSpeed: number = 0;
  modeCValid: boolean = false;
  modeC: number = 0;
  //???
  modeSNationality: string = null;

  //Classification
  //OPR Classification
  oprCategory: string;
  oprCategoryIndex: number;
  oprPlatform: string;
  oprPlatformIndex: number;

  //SYS Classification
  sysCls: string = "N/A";
  cls: string = "N/A";

  //Display Properties
  pic: string = "default"
  symbol: string = null;
  svgSymbol: string = null;
  newmiColor = [];
  cssColor: string = null;

  //More Data
  corrPlots: Set<CorrEntityID> = new Set();
  corrTracks: Set<CorrEntityID> = new Set();
  prevCorrPlots: Set<CorrEntityID> = new Set(); // for side view display
  trail: TrackTrail;
  vector: any;

  constructor(objectsPool: IObjectsPool, objectType: ObjectType, public trackID: string) {
    super(objectsPool, objectType, AspObject.generateId(objectType, trackID));

    this.trail = new TrackTrail();
    this.vector = new NeWMI.Geometry.Polyline();
  }

  private addTrail() {
    if (this.geometry == null)
      return;
    this.trail.addPoint(this.geometry);
  }

  updateVector() {
    if (this.geometry == null)
      return;

    let vectorHLength = this.speed * aspSettings.trackVectorLength() / 2;
    let vectorVLength = this.vertSpeed * aspSettings.trackVectorLength() / 2;

    const currPoint = new NeWMI.Geometry.Point({ x: this.geometry.x, y: this.geometry.y, z: this.geometry.z });
    // const futurePoint = utils.newmiPointByRangeAzimuth(currPoint, vectorHLength, this.heading, vectorVLength); // this will calc a future point...
    const futurePoint = new NeWMI.Geometry.Point({ x: this.vectorLong * units.RAD_TO_DEG, y: this.vectorLat * units.RAD_TO_DEG, z: this.vectorAlt });

    this.vectorHeading = NeWMI.Service.Math.MeasurementSvc.getAngle(this.geometry.x, this.geometry.y, this.vectorLong * units.RAD_TO_DEG, this.vectorLat * units.RAD_TO_DEG);

    this.vector.points.length = 0;
    this.vector.points.push(currPoint);
    this.vector.points.push(futurePoint);
  }

  protected updateDynamics(x, y, z, hdg, spd, vz) {
    super.updateDynamics(x, y, z, hdg, spd, vz);
    this.updateVector();
    if (this.trackStatus == enums.TrackStatus.Normal) {
      this.addTrail();
    }
  }

  handleMsg(msgObj: any): void {
    if (msgObj.TrackId != -1) {
      this.trackID = msgObj.TrackId;
    }
    else
      this.trackID = msgObj.TrackUUID;
    this.trackStatus = msgObj.TrackStat;

    if (msgObj.TrackType != null) {
      if (msgObj.TrackType != undefined)
        this.trackType = msgObj.TrackType;
    }

    // vector
    if (msgObj.HeadingEndLocationAltitude)
      this.vectorAlt = msgObj.HeadingEndLocationAltitude;
    else
      this.vectorAlt = msgObj.LocationAltitude;

    if (msgObj.HeadingEndLocationLat)
      this.vectorLat = msgObj.HeadingEndLocationLat;
    else
      this.vectorLat = msgObj.LocationLat;

    if (msgObj.HeadingEndLocationLong)
      this.vectorLong = msgObj.HeadingEndLocationLong;
    else
      this.vectorLong = msgObj.LocationLong;

    if (msgObj.Cls)
      this.cls = msgObj.Cls;
    else
      this.cls = "N/A";

    let loc: Location;
    if (this.dontupdateloc)
      loc = { altitude: msgObj.LocationAltitude, longitude: this.geometry.x, latitude: this.geometry.y };
    else
      loc = { altitude: msgObj.LocationAltitude, longitude: msgObj.LocationLong * units.RAD_TO_DEG, latitude: msgObj.LocationLat * units.RAD_TO_DEG };
    if (loc) {
      let vertSpeed = msgObj.VelocityZ;
      let speed = msgObj.VelocityXYZ;
      this.updateDynamics(loc.longitude + aspSettings.deltaX,
        loc.latitude + aspSettings.deltaY, loc.altitude,
        msgObj.Heading, speed, vertSpeed);

      this.velocityX = msgObj.VelocityX;
      this.velocityY = msgObj.VelocityY;
      if (msgObj.VelocityZ != undefined)
        this.velocityZ = msgObj.VelocityZ;
      else
        this.velocityZ = undefined;

    }
    this.savePrevCorrPlots();
    this.corrPlots.clear();

    //IFF Section
    // if (msgObj.IFFAvailable) {
    //   if (msgObj.Mode1) {
    //     this.mode1Valid = true;
    //     this.mode1 = msgObj.Mode1;
    //   }
    //   if (msgObj.Mode2) {
    //     this.mode2Valid = true;
    //     this.mode2 = parseInt(msgObj.Mode2);
    //   }
    //   if (msgObj.Mode3) {
    //     this.mode3Valid = true;
    //     this.mode3 = parseInt(msgObj.Mode3);
    //   }
    //   if (msgObj.ModeSAcIdent) {
    //     this.modeSValid = true;
    //     this.modeSAcIdent = msgObj.ModeSAcIdent;
    //     this.modeSAddress = msgObj.ModeSAddress;
    //     this.modeSAltitude = msgObj.ModeSAltitude;
    //     this.modeSSpeed = msgObj.ModeSSpeed;

    //     this.modeSNationality = msgObj.ModeSAcIdent;
    //   }
    //   if (msgObj.ModeC) {
    //     this.modeCValid = true;
    //     this.modeC = msgObj.ModeC;
    //   }
    // }
    // else {
    //   this.mode1Valid = false;
    //   this.mode2Valid = false;
    //   this.mode3Valid = false;
    //   this.modeSValid = false;
    //   this.modeCValid = false;
    // }

    // let datetime = msgObj.Time.split('-');
    // let date = datetime[0].split('.');
    // let time = datetime[1].split(':');
    // this.timeStamp = new Date(parseInt(date[2]) + 2000, parseInt(date[1]) - 1, parseInt(date[0]), time[0], time[1], time[2].split('.')[0]);

    this.timeStamp = new Date(msgObj.Time * 1000);
    console.log(this.timeStamp);
    console.log(this.timeStamp.toLocaleString());
    this.TimeOfArrival = Date.now();

    // this.symbol = enums.HostilityTitle(0);
    this.symbol = "fighter-plane";
    this.hostility = enums.Hostility.Unkn;
    this.svgSymbol = utils.getPlaneSymbol(this.symbol);

    if (msgObj.Hostility != null) {
      this.hostility = msgObj.Hostility;
    }

    //external track data
    if (msgObj.IsExternal) {
      this.isExternal = true;
      this.externalTN = msgObj.ExternalTN;
      if (msgObj.RemoteHostility != null) {
        this.remoteHostility = msgObj.RemoteHostility;
        if (this.userSetHostility == false)
          this.hostility = this.remoteHostility;
      }
    }

    this.newmiColor = aspSettings.colorOfIdentity(this.hostility);
    this.cssColor = utils.newmiColorToString(this.newmiColor);

    if (msgObj.trackingStatus)
      this.trackingStatus = msgObj.trackingStatus;
    else {
      this.trackingStatus = '';
    }

    if (this.trackType == enums.TrackType.ABT) {
      //sys clasification
      if (msgObj.AutomaticClassification) {
        if (msgObj.AutomaticClassification != "") {
          this.sysCls = msgObj.AutomaticClassification;
          this.dontupdateloc = false;
        }
      }
    }

    if (this.trackType == enums.TrackType.TBM) {
      this.maneuver = msgObj.Maneuvering;
      this.boostPhase = msgObj.BoostPhase;
    }

    // classification
    this.setClsPlt(msgObj.OperatorCategory, msgObj.OperatorPlatform);

    // vector pic
    let speedRatio: number = this.speed / this.MAX_SPEED_MS;
    if (this.trackType == enums.TrackType.TBM) {
      speedRatio = this.speed / this.MAX_SPEED_MS;
    }

    //radars data
    if (msgObj.SensorList) {
      // get length of json object
      let listlen = Object.keys(msgObj.SensorList).length;
      for (let i = 0; i < listlen; i++) {
        if (msgObj.SensorList[i]) {
          let snsobj = aspObjects.sensorsPool.getSensorBySensorID(msgObj.SensorList[i].SnsNum);
          if (snsobj)
            this.snsObjList.push(snsobj);
          this.snsIdList.push(msgObj.SensorList[i].SnsNum);
        }
      }
    }

    this.postHandleMsg(msgObj);
    this.ForceUpdate = true;
    this.notifyDataChange();
  }

  protected postHandleMsg(msgObj: any) {
    // To be implemented if needed
  }

  private savePrevCorrPlots() {
    for (let corrPlot of this.corrPlots) {
      if (!this.prevCorrPlots.has(corrPlot))
        this.prevCorrPlots.add(corrPlot);
    }
  }

  setClsPlt(cls: string, plt?: string) {
    if (cls != '' && cls != undefined) {
      this.oprCategory = cls;
      let clsData = AppInjector.get(ClsArmService).getSelf();
      if (clsData) {
        this.oprCategoryIndex = clsData.getIndexOfCat(this.oprCategory);
        if (plt != '' && plt != undefined) {
          this.oprPlatform = plt;
          this.oprPlatformIndex = clsData.getIndexOfPlt(this.oprCategory, this.oprPlatform);
        }
        else {
          this.oprPlatform = '';
          this.oprPlatformIndex = 0;
        }
      }
    }
    else {
      this.oprCategory = '';
      this.oprCategoryIndex = 0;
      this.oprPlatform = '';
      this.oprPlatformIndex = 0;
    }
  }

  // Get object searchable identifiers (to be used by Search function)
  getIdentifiers(): string[] {
    let identifiers = [];
    identifiers.push(this.trackID.toString());
    if (this.mode3Valid)
      identifiers.push(utils.getIFFString(this.mode3));
    return identifiers;
  }

  isValid() {
    return this.geometry != null;
  }

  // to be used by ARO table display
  aroTableFieldsWidth(): string[] {
    return [];
  }
  aroTableFields(): string[] {
    return [];
  }
  aroTableShowNationality(): boolean {
    return true;
  }

  // to be used by auxiliary DB label display
  auxDbLabelText(): string {
    return null;
  }

  // Get object title
  getTitle(): string {
    return `Track: ${this.trackID.toString()}`;
  }

  // Override AspObject
  // color for newmi drawing
  getColor() {
    return this.newmiColor;
  }

  // color for css html 
  getCssColor() {
    return this.cssColor
  }

  // gets the svg icon name
  getSymbol() {
    return this.symbol;
  }

  // gets full path and the icon
  getSvgSymbol() {
    return this.svgSymbol;
  }
}